import { useTranslation } from "react-i18next";
import { Icon } from "@/components/icon/icon";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEY, ICON_KEYS } from "@/icons/icon-registry";
import { SIGNAL_LEVEL } from "@/services/device-attributes/attributes/network/definitions";
import { isValidDate } from "@/utils/time";
/**
 * The icons for the signal levels
 */
export const signalLevelIconKeys: Record<SIGNAL_LEVEL, ICON_KEY> = {
    [SIGNAL_LEVEL.ERROR]: ICON_KEYS.SIGNAL_ERROR,
    [SIGNAL_LEVEL.VERY_LOW]: ICON_KEYS.SIGNAL_VERY_LOW,
    [SIGNAL_LEVEL.LOW]: ICON_KEYS.SIGNAL_LOW,
    [SIGNAL_LEVEL.OK]: ICON_KEYS.SIGNAL_MEDIUM,
    [SIGNAL_LEVEL.GOOD]: ICON_KEYS.SIGNAL_HIGH,
    [SIGNAL_LEVEL.EXCELLENT]: ICON_KEYS.SIGNAL_FULL,
    [SIGNAL_LEVEL.UNKNOWN]: ICON_KEYS.SIGNAL_UNKNOWN,
};

/**
 * The translation key prefix for the battery levels
 */
export const SIGNAL_LEVEL_TRANSLATION_NAMESPACE = NAMESPACES.ICONS;
export const SIGNAL_LEVEL_TRANSLATION_KEY_PREFIX = "signal-status";

export const signalLevelTooltipTranslationKeys = {
    [SIGNAL_LEVEL.VERY_LOW]: "tooltip.signal-very-low",
    [SIGNAL_LEVEL.LOW]: "tooltip.signal-low",
    [SIGNAL_LEVEL.OK]: "tooltip.signal-ok",
    [SIGNAL_LEVEL.GOOD]: "tooltip.signal-good",
    [SIGNAL_LEVEL.EXCELLENT]: "tooltip.signal-excellent",
    [SIGNAL_LEVEL.UNKNOWN]: "tooltip.signal-unknown",
    [SIGNAL_LEVEL.ERROR]: "tooltip.signal-error",
};

export const signalLevelLabelTranslationKeys = {
    [SIGNAL_LEVEL.VERY_LOW]: "label.signal-very-low",
    [SIGNAL_LEVEL.LOW]: "label.signal-low",
    [SIGNAL_LEVEL.OK]: "label.signal-ok",
    [SIGNAL_LEVEL.GOOD]: "label.signal-good",
    [SIGNAL_LEVEL.EXCELLENT]: "label.signal-excellent",
    [SIGNAL_LEVEL.UNKNOWN]: "label.signal-unknown",
    [SIGNAL_LEVEL.ERROR]: "label.signal-error",
};

interface SignalStatusIconProps {
    lastContactTime: Date | undefined;
    signalLevel: SIGNAL_LEVEL;
    includeLabel?: boolean;
    includeTooltip?: boolean;
}

/**
 * SignalStatusIcon displays the signal status of a device
 */
export function SignalStatusIcon({
    lastContactTime,
    signalLevel,
    includeLabel = true,
    includeTooltip = true,
}: SignalStatusIconProps) {
    const { t } = useTranslation(NAMESPACES.ICONS, { keyPrefix: SIGNAL_LEVEL_TRANSLATION_KEY_PREFIX });
    // Detect the 'Invalid Date' object or other malformed dates
    const dateValid = isValidDate(lastContactTime);

    const tooltip = dateValid
        ? t(signalLevelTooltipTranslationKeys[signalLevel])
        : t(signalLevelLabelTranslationKeys[signalLevel]);

    return (
        <Icon
            icon={signalLevelIconKeys[signalLevel]}
            label={includeLabel ? t(signalLevelLabelTranslationKeys[signalLevel]) : undefined}
            tooltip={includeTooltip ? tooltip : undefined}
        />
    );
}
